import { Box, styled } from "@mui/material";
import { cloneDeep } from "lodash";
import { useState, useEffect, Fragment, useContext } from "react";
import EndGameModal from "../../components/endGameModal";
import colors from "../../theme/colors";
import fonts from "../../theme/fonts";
import { GAME_STATE } from "../../types/game";
import { BaseMatchState, GAME_TYPE, MATCH_TYPE } from "../../types/match";
import { PLAYER } from "../../types/players";
import {
  getHalvesInitState,
  getNextHalvesState,
  endGame,
  getCurrentRoundIdx,
} from "../../util/halvesLogic";
import ResultScreen from "../results/results";
import AllButtons from "../../components/AllButtons";
import { mediaQueries } from "../../theme/breakpoints";
import { HalvesGameState, Round, ROUND_TYPE } from "../../types/halves";
import { ScreenSizeContext } from "../../contexts/ScreenSizeContext";

interface HalvesProps {
  matchState: BaseMatchState;
  endLeg: Function;
  rematch: Function;
  restart: boolean;
  setRestart: Function;
  numPlayers: number;
  rounds: Round[];
}

export default function Halves({
  matchState,
  numPlayers,
  rounds,
  endLeg,
  rematch,
  restart,
  setRestart,
}: HalvesProps) {
  const { tablet, mobile } = useContext(ScreenSizeContext);
  const [gameState, setGameState] = useState<HalvesGameState>(
    getHalvesInitState(numPlayers, rounds)
  );
  const [oldStates, setOldStates] = useState<HalvesGameState[]>([]);
  const [inUndoState, setInUndoState] = useState<boolean>(false);

  matchState.gameType = GAME_TYPE.HALVES;
  const botTurn =
    matchState.matchType === MATCH_TYPE.BOT &&
    gameState.currentPlayer === PLAYER.SECOND;
  const disableButton = botTurn;

  useEffect(() => {
    if (restart) {
      rematchHandler();
    }
    // eslint-disable-next-line
  }, [restart]);

  const onClickHandler = async (number: number, multiplier: number) => {
    if (
      !gameState ||
      gameState.gameState === GAME_STATE.PENDING ||
      gameState.gameState === GAME_STATE.FINISHED
    )
      return;
    let newState: HalvesGameState;

    const oldGameState = cloneDeep(gameState);
    setOldStates([...oldStates, oldGameState]);
    newState = getNextHalvesState({ number, multiplier }, gameState);
    setGameState(newState);
    setInUndoState(false);
  };

  const undoHandler = async () => {
    if (oldStates.length === 0) return;
    setInUndoState(true);
    const states = [...oldStates];
    let latestOldState = states[states.length - 1];
    const updatedOldStates: HalvesGameState[] = states.slice(0, -1);
    setOldStates(updatedOldStates);
    setGameState(latestOldState);
  };

  const endGameHandler = async () => {
    let newState: HalvesGameState;
    newState = endGame(gameState);
    endLeg(newState);
    setOldStates([]);
    setGameState(newState);
  };

  const newGameHandler = () => {
    const newGameState = getHalvesInitState(numPlayers, rounds);
    newGameState.gameState = GAME_STATE.ACTIVE;
    setGameState(newGameState);
  };

  const rematchHandler = () => {
    rematch();
    const newGameState = getHalvesInitState(numPlayers, rounds);

    setGameState(newGameState);
    setRestart(false);
  };

  const getButtonsColor = (): PLAYER => {
    const { players, rounds, currentRound } = gameState;
    if (players.length === 1) return PLAYER.FIRST;
    let playerIdx = 0;
    for (let i = 0; i < players.length; i++) {
      if (players[i].name === gameState.currentPlayer) playerIdx = i;
    }
    if (players.length % 2 === 0) {
      return playerIdx % 2 === 0 ? PLAYER.FIRST : PLAYER.SECOND;
    }
    let roundIdx = getCurrentRoundIdx(currentRound, rounds);
    return (playerIdx % 2 === 0 && roundIdx % 2 === 0) ||
      (playerIdx % 2 !== 0 && roundIdx % 2 !== 0)
      ? PLAYER.FIRST
      : PLAYER.SECOND;
  };

  return (
    <Wrapper>
      {gameState.gameState !== GAME_STATE.FINISHED && (
        <Fragment>
          <TopRow>
            <RoundType>
              {gameState.currentRound.type !== ROUND_TYPE.SEL_NUMBER
                ? gameState.currentRound.type
                : `${gameState.currentRound.number}s`}
            </RoundType>
            <ThrowBox>
              <Box display="flex" justifyContent="center">
                <Box margin="auto">{gameState.currentPlayer}</Box>
              </Box>
              <Box display="flex" flexDirection="column">
                <Box>Throw:</Box>
                <Box>{gameState.throwNumber}</Box>
              </Box>
            </ThrowBox>
          </TopRow>
          <BottomRow>
            <ScoreColumn>
              <PlayersWrapper>
                {gameState.players.map((p) => {
                  return (
                    <PlayerWrapper
                      key={`score_${p.name}`}
                      active={gameState.currentPlayer === p.name}
                    >
                      <Box>{p.name}</Box>
                      <Box>{p.score}</Box>
                    </PlayerWrapper>
                  );
                })}
              </PlayersWrapper>
            </ScoreColumn>
            <ButtonsColumn>
              {(tablet || mobile) && (
                <PlayersWrapper>
                  {gameState.players.map((p) => {
                    return (
                      <PlayerWrapper
                        key={`score_${p.name}`}
                        active={gameState.currentPlayer === p.name}
                      >
                        <Box>{p.name}</Box>
                        <Box>{p.score}</Box>
                      </PlayerWrapper>
                    );
                  })}
                </PlayersWrapper>
              )}
              <AllButtons
                onClickHandler={onClickHandler}
                botTurn={disableButton}
                undoHandler={undoHandler}
                inUndoState={inUndoState}
                currentPlayer={getButtonsColor()}
                trueColor={
                  gameState.currentRound.type === ROUND_TYPE.SEL_COLOR ||
                  gameState.currentRound.type === ROUND_TYPE.COLOR_DIFF
                }
              />
            </ButtonsColumn>
            <ScoreColumn>
              <RoundsWrapper>
                <RoundTitle>Rounds:</RoundTitle>
                {gameState.rounds.map((r, i) => {
                  return (
                    <Box key={`round_${i}`}>
                      {r.type !== ROUND_TYPE.SEL_NUMBER
                        ? r.type
                        : `${r.number}s`}
                    </Box>
                  );
                })}
              </RoundsWrapper>
            </ScoreColumn>
          </BottomRow>
        </Fragment>
      )}
      {gameState.gameState === GAME_STATE.FINISHED && (
        <ResultScreen
          newGameHandler={newGameHandler}
          matchState={matchState}
          rematchHandler={rematchHandler}
        />
      )}
      <EndGameModal
        open={gameState.gameState === GAME_STATE.PENDING}
        undo={undoHandler}
        endGame={endGameHandler}
      />
    </Wrapper>
  );
}

const Wrapper = styled(Box)({
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-evenly",
  height: "inherit",
  "@media (max-width: 600px)": {
    overflowX: "hidden",
  },
});

const TopRow = styled(Box)({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  fontSize: fonts.size.medium,
  fontFamily: fonts.family.chalk,
  color: colors.lightBeige,
  gap: "5px",
  margin: "auto",
  "@media (max-width: 600px)": {
    fontSize: fonts.size.small,
  },
});

const BottomRow = styled(Box)({
  display: "flex",
  flex: 5,
  justifyContent: "space-evenly",
  borderTop: "4px double black",
  maxHeight: `${window.innerHeight * 0.8}px`,
  "@media(max-width: 960px)": {
    border: "none",
    // padding: "0px 10px"
    // maxWidth: "95%",
  },
  "@media(max-width: 650px)": {
    width: "95%",
    margin: "0 auto",
    // alignSelf: "center"
  },
});

const ThrowBox = styled(Box)({
  display: "flex",
  justifyContent: "space-around",
  // flexDirection: "column",
  color: colors.lightBeige,
  margin: "auto",
  border: "4px double black",
  padding: " 5px 25px",
  width: "200px",
  fontSize: fonts.size.mediumLarge,
  [mediaQueries.mobile]: {
    fontSize: fonts.size.medium,
    width: "130px",
  },
});

const ScoreColumn = styled(Box)({
  display: "flex",
  flex: 1,
  maxHeight: `100%`,
  "@media(max-width: 960px)": {
    display: "none",
  },
});

const ButtonsColumn = styled(Box)({
  display: "flex",
  flexDirection: "column",
  flex: 4,
  justifyContent: "center",
  height: "100%",
  "@media(max-width: 600px)": {
    maxWidth: "90vw",
  },
});

// const PreviousShotsButton = styled(Button)({
//   backgroundColor: colors.lightGreen,
//   color: colors.darkGray,
//   width: "90%",
//   textTransform: "none",
// });

const RoundType = styled(Box)({
  fontSize: fonts.size.extraLarge,
});

const PlayersWrapper = styled(Box)({
  display: "flex",
  flexWrap: "wrap",
  justifyContent: "space-around",
  width: "100%",
  gap: "15px",
  overflowY: "scroll",
  scrollbarWidth: "none",
  "&::-webkit-scrollbar": {
    display: "none",
  },
  [mediaQueries.tablet]: {
    justifyContent: "center",
  },
  [mediaQueries.mobile]: {
    justifyContent: "space-between",
    gap: "5px",
  },
});
const PlayerWrapper = styled(Box)<{ active: boolean }>(({ active }) => ({
  display: "flex",
  flexDirection: "column",
  color: active ? colors.darkGray : colors.lightBeige,
  fontSize: fonts.size.large,
  backgroundColor: active ? colors.lightBeige : "",
  height: "100px",
  padding: "0 15px",
  justifyContent: "center",
  [mediaQueries.tablet]: {
    justifyContent: "center",
    fontSize: fonts.size.medium,
  },
  [mediaQueries.mobile]: {
    fontSize: fonts.size.mediumLarge,
    height: "70px",
    padding: "3px",
  },
}));

const RoundsWrapper = styled(Box)({
  display: "flex",
  flexDirection: "column",
  margin: "0 auto",
  gap: "15px",
  fontSize: fonts.size.mediumLarge,
  color: colors.lightBeige,
  overflowY: "scroll",
  scrollbarWidth: "none",
  "&::-webkit-scrollbar": {
    display: "none",
  },
});

const RoundTitle = styled(Box)({
  fontSize: fonts.size.large,
  fontWeight: "bold",
});
